/**
 * VistoeraTASK-00523 - pure folder-tree model shared by the Image Asset Center
 * (管理入口) and the Canvas Image Asset Picker (选择入口).
 *
 * There is ONE canonical tree (`parent_id` on the collection model). Nothing
 * here knows about depth limits: every walk is iterative and cycle-guarded so a
 * 15-level (or 500-level) tree can never blow the stack or loop forever.
 */

export interface FolderLike {
  id: string
  name: string
  parent_id?: string | null
  is_system?: boolean
  system_key?: string | null
}

export interface FolderNode<T extends FolderLike = FolderLike> {
  folder: T
  depth: number
  children: FolderNode<T>[]
}

/** User folders only (the system 未整理图片 entry is a separate, virtual entry). */
export function userFolders<T extends FolderLike>(all: readonly T[]): T[] {
  return all.filter((f) => !f.is_system && !f.system_key)
}

/**
 * Build the forest of user folders (server / creation order preserved).
 * Orphans (parent missing / system / cyclic) are promoted to roots so a damaged
 * row can never hide a subtree.
 */
export function buildFolderForest<T extends FolderLike>(all: readonly T[]): FolderNode<T>[] {
  const folders = userFolders(all)
  const byId = new Map(folders.map((f) => [f.id, f]))
  const childrenOf = new Map<string, T[]>()
  const roots: T[] = []
  for (const f of folders) {
    const pid = f.parent_id
    if (pid && pid !== f.id && byId.has(pid)) {
      const list = childrenOf.get(pid) ?? []
      list.push(f)
      childrenOf.set(pid, list)
    } else {
      roots.push(f)
    }
  }
  const seen = new Set<string>()
  const build = (rootFolders: T[]): FolderNode<T>[] => {
    const out: FolderNode<T>[] = []
    // iterative DFS - unbounded depth must never hit the call-stack limit
    const stack: Array<{ folder: T; depth: number; into: FolderNode<T>[] }> = [...rootFolders]
      .reverse()
      .map((folder) => ({ folder, depth: 0, into: out }))
    while (stack.length) {
      const cur = stack.pop()!
      if (seen.has(cur.folder.id)) continue
      seen.add(cur.folder.id)
      const node: FolderNode<T> = { folder: cur.folder, depth: cur.depth, children: [] }
      cur.into.push(node)
      const kids = [...(childrenOf.get(cur.folder.id) ?? [])].reverse()
      for (const kid of kids) stack.push({ folder: kid, depth: cur.depth + 1, into: node.children })
    }
    return out
  }
  const forest = build(roots)
  // Cycles with no root are unreachable from `roots`: surface them as roots too.
  const leftover = folders.filter((f) => !seen.has(f.id))
  if (leftover.length) forest.push(...build(leftover))
  return forest
}

/** Root -> folder chain (empty when the id is unknown). Loop-safe. */
export function folderPath<T extends FolderLike>(all: readonly T[], folderId: string | null): T[] {
  if (!folderId) return []
  const byId = new Map(all.map((f) => [f.id, f]))
  const chain: T[] = []
  const seen = new Set<string>()
  let cur = byId.get(folderId)
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id)
    chain.push(cur)
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined
  }
  return chain.reverse()
}

/** Ids that must be expanded so `folderId` is visible. */
export function ancestorIds<T extends FolderLike>(all: readonly T[], folderId: string | null): string[] {
  const path = folderPath(all, folderId)
  return path.slice(0, -1).map((f) => f.id)
}

export interface VisibleFolderRow<T extends FolderLike = FolderLike> {
  folder: T
  depth: number
  hasChildren: boolean
  expanded: boolean
}

/** Flatten the forest honouring the expanded set (only visible rows are rendered). */
export function flattenVisible<T extends FolderLike>(
  forest: readonly FolderNode<T>[],
  expanded: ReadonlySet<string>,
): VisibleFolderRow<T>[] {
  const rows: VisibleFolderRow<T>[] = []
  const stack = [...forest].reverse()
  while (stack.length) {
    const node = stack.pop()!
    const hasChildren = node.children.length > 0
    const isOpen = hasChildren && expanded.has(node.folder.id)
    rows.push({ folder: node.folder, depth: node.depth, hasChildren, expanded: isOpen })
    if (isOpen) for (let i = node.children.length - 1; i >= 0; i -= 1) stack.push(node.children[i])
  }
  return rows
}

export type BreadcrumbSegment<T extends FolderLike = FolderLike> =
  | { kind: 'root' }
  | { kind: 'folder'; folder: T; isCurrent: boolean }
  /** Collapsed middle levels (clickable via an overflow menu). */
  | { kind: 'ellipsis'; hidden: T[] }

/**
 * `全部图片 > 25 秋冬 > 女装 > 连衣裙`. Deep paths collapse the MIDDLE:
 * `全部图片 > … > 连衣裙 > 款式 A > 白色` (every hidden level stays reachable).
 */
export function buildBreadcrumb<T extends FolderLike>(path: readonly T[], maxFolderSegments = 4): BreadcrumbSegment<T>[] {
  const segs: BreadcrumbSegment<T>[] = [{ kind: 'root' }]
  const keepTail = Math.max(1, maxFolderSegments - 1)
  if (path.length <= maxFolderSegments) {
    path.forEach((folder, i) => segs.push({ kind: 'folder', folder, isCurrent: i === path.length - 1 }))
    return segs
  }
  const hidden = path.slice(0, path.length - keepTail)
  segs.push({ kind: 'ellipsis', hidden })
  path.slice(path.length - keepTail).forEach((folder, i, arr) =>
    segs.push({ kind: 'folder', folder, isCurrent: i === arr.length - 1 }),
  )
  return segs
}

// ---------------------------------------------------------------------------
// Picker scope -> canonical list query (ONE mapping; mirrors Asset Center)
// ---------------------------------------------------------------------------

export type PickerTeamScope =
  | { kind: 'my_all' }
  | { kind: 'my_created' }
  | { kind: 'team_all' }
  | { kind: 'team_members' }
  | { kind: 'unsorted' }
  | { kind: 'folder'; folderId: string }

export interface TeamListQuery {
  view: 'mine' | 'shared' | 'all'
  collection_id?: string
  exclude_me?: boolean
}

export function teamQueryForScope(scope: PickerTeamScope): TeamListQuery {
  switch (scope.kind) {
    case 'my_all':
      return { view: 'all' }
    case 'my_created':
      return { view: 'mine' }
    case 'team_all':
      return { view: 'shared' }
    case 'team_members':
      return { view: 'shared', exclude_me: true }
    case 'unsorted':
      return { view: 'all', collection_id: 'unsorted' }
    case 'folder':
      // A folder shows its own + every descendant's images the viewer may access.
      return { view: 'all', collection_id: scope.folderId }
  }
}

export function scopeKey(scope: PickerTeamScope): string {
  return scope.kind === 'folder' ? `folder:${scope.folderId}` : scope.kind
}

/**
 * Would the CURRENT scope list this item? Used to decide whether a just-uploaded
 * / just-captured image may be merged straight into the visible grid (so a
 * phone upload into 未整理图片 never appears inside an unrelated folder).
 * The server remains the source of truth for the next real list load.
 */
export function itemMatchesScope(
  item: { collection_id?: string | null; created_by_user_id?: string | null; visibility?: string | null },
  scope: PickerTeamScope,
  folders: readonly FolderLike[],
  meUserId?: string | null,
): boolean {
  const cid = item.collection_id ?? null
  const unsortedId = folders.find((f) => f.system_key === 'unsorted')?.id ?? null
  const inUnsorted = !cid || cid === unsortedId
  switch (scope.kind) {
    case 'my_all':
      return true
    case 'my_created':
      return !meUserId || !item.created_by_user_id || item.created_by_user_id === meUserId
    case 'team_all':
      return (item.visibility ?? 'workspace') === 'workspace'
    case 'team_members':
      return (
        (item.visibility ?? 'workspace') === 'workspace' && !!item.created_by_user_id && item.created_by_user_id !== meUserId
      )
    case 'unsorted':
      return inUnsorted
    case 'folder':
      return !!cid && folderPath(folders, cid).some((f) => f.id === scope.folderId)
  }
}

/** Local-upload destination: the folder being browsed, otherwise 未整理图片 (null). */
export function uploadTargetForScope(scope: PickerTeamScope): string | null {
  return scope.kind === 'folder' ? scope.folderId : null
}

/** A just-uploaded own+shared image never belongs in 团队成员创建. */
export function scopeAfterLocalUpload(scope: PickerTeamScope): PickerTeamScope {
  return scope.kind === 'team_members' ? { kind: 'my_all' } : scope
}
